import { Component, effect, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { SpinnerComponent } from "app/components/ui-kit/loaders/spinner/spinner.component";
import { SvgLoaderComponent } from "app/components/ui-kit/images/svg-loader/svg-loader.component";
import { AiBlobComponent } from "app/components/distinct-components/ai-visual/ai-blob/ai-blob.component";

import { TtsStreamingService } from 'app/services/tts-stream.service';

import { debounce, withRetry } from './utils/async-utils';
import { getAudioDuration, playSoundAndWait } from './utils/audio-utils';
import { TypingDotsComponent } from "app/components/ui-kit/loaders/typing-dots/typing-dots.component";

import { blankDataModel } from 'app/constants/conversation-data-models';
import { DEFAULT_GROQ_CHAT_MODEL } from 'app/constants/ai-model-constants';
import { environment } from 'env/environment';
import { TimeAndDateService } from 'app/services/time-and-date-service';
import { AppStateService } from 'app/services/app-state.service';
import { FlightOptionService } from 'app/services/flight-option-service';
import { ConversationService } from 'app/services/conversation.service';
import { shouldResetFlightOptionId } from '../../utils/data-model-utils';

function getMicrophoneErrorMessage(error: unknown): string {
  const errorName = error instanceof Error ? error.name : '';

  switch (errorName) {
    case 'NotFoundError':
      return 'No microphone is available. Connect or select a microphone, then try again.';
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone access is blocked. Allow it in your browser settings, then try again.';
    case 'NotReadableError':
      return 'Your microphone is being used by another app. Close it and try again.';
    case 'NotSupportedError':
      return 'Voice input is not supported in this browser.';
    default:
      return 'Voice input could not start. Check your microphone and try again.';
  }
}

@Component({
  selector: 'app-agent-voice-io',
  standalone: true,
  imports: [FormsModule, CommonModule, SpinnerComponent, SvgLoaderComponent, AiBlobComponent, TypingDotsComponent],
  templateUrl: './agent-voice.component.html',
  styleUrls: ['./agent-voice.component.scss']
})
export class AgentVoiceComponent implements OnInit, OnDestroy {
  currentConversation: any = [];
  currentDataModel: any;
  currentIntent: any;
  user: any;

  currentSegmentChunks: Blob[] = [];
  transcription: string = '';
  response: string = '';

  isInitListen: boolean = false;
  hasPlayedInitSound: boolean = false;

  private isMobileSafari: boolean = false;
  private mobileIdleTimeoutId: any = null;

  mediaRecorder: MediaRecorder | null = null;
  audioContext: AudioContext | null = null;
  analyser: AnalyserNode | null = null;
  source: any | null = null;
  mediaStream: MediaStream | null = null;
  silenceCheckerId: number | null = null;
  silenceStart: number | null = null;
  segmentStartTime: number | null = null;
  smoothedVolume: number = 0;
  lastSegmentFlushTime: number = 0;
  lastHighVolumeTime: number = 0;
  idleTimeoutDuration: number = 0;
  silenceThreshold: number = 0.05;
  silenceDelay: number = 1800;
  minUtteranceDuration: number = 300;
  segmentHadSound: boolean = false;
  isProcessingSpeech: boolean = false;
  isMicActivated: boolean = false;
  isRecording: boolean = false;
  recordingError: string | null = null;
  dynamicStatusText: string = 'Analyzing...';

  public audioElement: HTMLAudioElement | null = null;
  mimeType: string | undefined;

  public isPausedForTTS: boolean = false;
  private ttsCooldown: number = 250;

  // development related or yet to be fully realized
  intent: string | null = null;
  dataModel: any | null = null;

  private cleanupHandler = this.cleanupOnExit.bind(this);

  constructor(private ttsService: TtsStreamingService, private timeService: TimeAndDateService, public appState: AppStateService, private flightOptions: FlightOptionService, private conversationService: ConversationService) {
    effect(() => {
      this.currentConversation = this.appState.agent.currentConversation();
    });
    effect(() => {
      this.currentDataModel = this.appState.agent.currentDataModel();
    });
    effect(() => {
      this.user = this.appState.user.profile();
    });
    effect(() => {
      const resp = this.appState.agent.singleTTS();
      if (resp && resp.body) {
        this.playSingleResponseTTS(resp);
      }
    }, { allowSignalWrites: true })
  }

  ngOnInit() {
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 1024;
    this.audioElement = new Audio();

    const availableMimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    this.mimeType = availableMimeType;

    this.isMobileSafari = /iPhone|iPad|iPod/i.test(navigator.userAgent) && /Safari/i.test(navigator.userAgent);

    document.addEventListener('visibilitychange', this.cleanupHandler);
    window.addEventListener('pagehide', this.cleanupHandler);
    window.addEventListener('beforeunload', this.cleanupHandler);
  }

  // Handle user click of the audio button
  async onClickAudioButton() {
    if (this.isInitListen) {
      return;
    }
    this.isInitListen = true;
    try {
      if (!this.isRecording) {
        await this.startContinuousRecording();
        if (this.isRecording && this.isMobileSafari && !this.mobileIdleTimeoutId) {
          this.resetMobileIdleTimer();
        }
      } else {
        this.stopContinuousRecording();
        this.isPausedForTTS = false;
        this.isProcessingSpeech = false;
        this.audioElement.pause();
        this.audioElement.src = '';
      }
    } finally {
      this.isInitListen = false;
    }
  }

  async startContinuousRecording(): Promise<void> {
    this.recordingError = null;
    if (!this.hasPlayedInitSound) {
      this.hasPlayedInitSound = true;
      await playSoundAndWait(this.audioElement!, 'assets/woop.wav');
    }
    // Reset segment tracking variables.
    this.currentSegmentChunks = [];
    this.segmentHadSound = false;
    this.segmentStartTime = null;
    this.silenceStart = null;

    try {
      // Reacquire the media stream if it's missing or inactive.
      if (!this.isMicActivated || !this.mediaStream || !this.mediaStream.active) {
        this.isMicActivated = true;
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      // Check and (re)initialize the AudioContext if necessary.
      if (!this.audioContext || this.audioContext.state === 'closed') {
        this.audioContext = new AudioContext();
        this.analyser = this.audioContext.createAnalyser(); // Recreate analyser
        this.analyser.fftSize = 1024;
      } else if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Create a new source from the media stream.
      this.source = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.source.connect(this.analyser);

      // Create a new MediaRecorder with the media stream.
      this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: this.mimeType });
      this.mediaRecorder.ondataavailable = (event) => {
        if (this.isPausedForTTS || this.isProcessingSpeech) {
          return;
        }
        if (event.data.size > 0) {
          // If this is the first chunk in a new segment, record the start time.
          if (this.currentSegmentChunks.length === 0) {
            this.segmentStartTime = Date.now();
          }
          this.currentSegmentChunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
      };

      // Start recording with a timeslice (e.g., 750 ms).
      this.mediaRecorder.start(750);
      this.isRecording = true;

      this.checkSilence();
    } catch (error) {
      console.warn('Voice input could not start:', error);
      this.isRecording = false;
      this.isMicActivated = false;
      this.recordingError = getMicrophoneErrorMessage(error);
      this.releaseRecordingResources();
    }
  }

  // Halt the current media recorder
  async stopContinuousRecording() {
    try {
      this.isRecording = false;
      this.isPausedForTTS = false;
      this.isProcessingSpeech = false;
      this.isMicActivated = false;

      if (this.mediaRecorder) {
        this.mediaRecorder.stop();
        this.mediaRecorder = null;
      }

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => {
          track.stop();
        });
        this.mediaStream = null;
      }

      if (this.source) {
        this.source.disconnect();
        this.source = null;
      }

      if (this.silenceCheckerId) {
        cancelAnimationFrame(this.silenceCheckerId);
        this.silenceCheckerId = null;
      }

      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.suspend();
      }

    } catch (error) {
      console.error('Error stopping continuous recording:', error);
    }
  }

  // Used to restart the process when non speech is detected
  stopAndRestartContinuousRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.onstop = () => {
        this.currentSegmentChunks = [];
        this.segmentHadSound = false;
        this.segmentStartTime = null;
        this.silenceStart = null;
        this.startContinuousRecording();
      };
      this.mediaRecorder.stop();
    } else {
      this.startContinuousRecording();
    }
  }

  // Watch audio stream for spoken words
  checkSilence(): void {
    const alpha = 0.3;
    const speechOnThreshold = this.silenceThreshold;
    const speechOffThreshold = this.silenceThreshold * 0.8;

    const check = () => {
      if (this.isPausedForTTS) {
        this.silenceCheckerId = requestAnimationFrame(check);
        return;
      }

      if (!this.silenceCheckerId) {
        return; // Exit if recording is off
      }

      if (!this.analyser || !this.mediaRecorder) {
        this.silenceCheckerId = requestAnimationFrame(check);
        return;
      }

      const dataArray = new Float32Array(this.analyser.frequencyBinCount);
      this.analyser.getFloatTimeDomainData(dataArray);
      const rms = Math.sqrt(dataArray.reduce((sum, val) => sum + val * val, 0) / dataArray.length);

      // Update smoothedVolume using exponential moving average.
      this.smoothedVolume = this.smoothedVolume
        ? alpha * rms + (1 - alpha) * this.smoothedVolume
        : rms;

      if (this.smoothedVolume >= speechOnThreshold) {
        this.segmentHadSound = true;
        this.silenceStart = null;
        this.lastHighVolumeTime = Date.now(); // update when we detect speech
      } else if (this.smoothedVolume < speechOffThreshold) {
        if (this.silenceStart === null) {
          this.silenceStart = Date.now();
        }
      }

      // Check if silence has persisted.
      if (this.silenceStart && Date.now() - this.silenceStart > this.silenceDelay) {
        // If speech was detected recently (within the last 500ms), cancel the flush.
        if (Date.now() - this.lastHighVolumeTime < 1000) {
          // Recent speech indicates we're still talking, so cancel flush.
          this.silenceStart = null;
        } else {
          if (this.currentSegmentChunks.length > 0 && this.segmentHadSound) {
            this.mediaRecorder?.requestData();
            this.dynamicStatusText = "Analyzing...";
            setTimeout(() => {
              const segmentDuration = this.segmentStartTime ? (Date.now() - this.segmentStartTime) : 0;
              if (segmentDuration >= this.minUtteranceDuration) {
                const audioBlob = new Blob(this.currentSegmentChunks, { type: this.mimeType });
                if (audioBlob.size >= 1000) {
                  this.debounceProcessSegment(audioBlob);
                } else {
                  console.warn("Segment too small, skipping. Size:", audioBlob.size);
                }
              } else {
                console.warn("Segment too short, skipping.");
              }
              // Reset state for the next segment.
              this.currentSegmentChunks = [];
              this.segmentHadSound = false;
              this.segmentStartTime = null;
              this.silenceStart = null;
            }, 50);
          }
        }
      }
      this.silenceCheckerId = requestAnimationFrame(check);
    };

    this.silenceCheckerId = requestAnimationFrame(check);
  }

  // Check the current segment for sound and quality
  private debounceProcessSegment = debounce((audioBlob: Blob) => {
    if (this.isMobileSafari) {
      this.resetMobileIdleTimer();
    }
    this.processAudioSegment(audioBlob);
    this.dynamicStatusText = "Optimizing Speech...";
  }, 100);

  // Updated processAudioSegment that checks duration and skips invalid segments.
  private async processAudioSegment(audioBlob: Blob): Promise<void> {
    const duration = await getAudioDuration(audioBlob, this.audioContext);
    if (duration < 0.01) {
      this.isProcessingSpeech = false;
      return;
    }
    this.isProcessingSpeech = true;

    try {
      this.dynamicStatusText = "Transcribing...";
      const transcription = await withRetry(() => this.transcribeAudioBlob(audioBlob, this.mimeType!));
      if (transcription) {
        if (transcription === 'NON-SPEECH-DATA') {
          this.stopAndRestartContinuousRecording();
          this.isProcessingSpeech = false;
          return;
        }
        this.transcription = transcription;
        await this.sendQuery(this.transcription);
      }
    } catch (err) {
      console.error("Error processing audio segment:", err);
    }
  }

  // Converts the blob to base64 and sends it to the backend for transcription.
  private async transcribeAudioBlob(audioBlob: Blob, mimeType: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64data = reader.result as string;
          const audioBase64 = base64data.split(',')[1];
          const ext = this.mimeType === 'audio/webm' ? 'webm' : 'mp4';
          const payload = { audioBase64, ext };

          const response = await fetch(`https://${environment.apiBaseURL}.cloudfunctions.net/getVoiceTranscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`Transcription request failed with status ${response.status}`);
          }
          const data = await response.json();

          const threshold = this.isMobileSafari ? 0.09 : 0.05;
          if (data.segments[0].no_speech_prob > threshold) {
            resolve('NON-SPEECH-DATA');
          } else {
            const isoTimestamp = this.timeService.getIsoTimestamp();
            this.appState.agent.currentConversation.update(convo => [
              ...convo,
              { timestamp: isoTimestamp, user: data.text, agent: '' }
            ]);
            resolve(data.text);
          }
        } catch (error) {
          console.error('Transcription error:', error);
          reject(error);
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };
      reader.readAsDataURL(audioBlob);
    });
  }

  // Sends the final transcribed query to the backend.
  async sendQuery(query: string): Promise<void> {
    const userData = {
      userCurrentLocation: this.user.currentLocation,
      preferences: this.user.preferences,
      memberships: this.user.memberships,
    }
    try {
      this.appState.agent.isHandlingQuery.set(true);
      this.dynamicStatusText = "Reasoning...";
      const dataModel = this.currentConversation.length > 1 ? this.currentDataModel : blankDataModel;
      const completedHistory = this.currentConversation
        .filter(entry => entry.agent !== '')
        .slice(-30)
        .map(({ dataModel, ...rest }) => rest);
      const payload = { userQuery: query, history: completedHistory, model: DEFAULT_GROQ_CHAT_MODEL, dataModel: dataModel, userData: userData };
      const response = await fetch(`https://${environment.apiBaseURL}.cloudfunctions.net/getAiResponseVoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        this.appState.agent.isHandlingQuery.set(false);
        const errorText = await response.text();
        console.error('Query fetch failed:', response.status, errorText);
        throw new Error(`Query failed: ${response.status} - ${errorText}`);
      }

      let intent = '';
      let newDataModel: any = {};

      try {
        intent = response.headers.get('X-Intent') || '';
        newDataModel = JSON.parse(response.headers.get('X-Data-Model') || '{}');
        this.response = response.headers.get('X-Response-Text') || '';

      } catch (error) {
        this.appState.agent.isHandlingQuery.set(false);
        console.error('Error parsing query headers:', error);
        this.intent = '';
        this.dataModel = {};
      }

      const oldDataModel = this.currentDataModel;
      if (shouldResetFlightOptionId(oldDataModel, newDataModel)) {
        newDataModel.userFlightOptionId = null;
      } else {
        newDataModel.userFlightOptionId = oldDataModel?.userFlightOptionId ?? null;
      }

      const isValid = await this.flightOptions.isFlightDataModelValid(newDataModel);
      this.appState.agent.currentDataModel.set(newDataModel);
      this.appState.agent.currentIntent.set(intent);

      if (isValid) {
        await this.conversationService.updateCurrentConversationData(
          this.currentConversation,
          newDataModel,
          intent || 'unknown'
        );
        this.appState.agent.isHandlingQuery.set(false);
        this.stopContinuousRecording();
        return;
      } else {
        this.dynamicStatusText = 'Agent Responding...';
        this.appState.agent.currentConversation.update(convo => {
          const updated = [...convo];
          const last = { ...updated[updated.length - 1], agent: this.response };
          updated[updated.length - 1] = last;
          return updated;
        });
        const updatedConversation = this.appState.agent.currentConversation();
        await this.conversationService.updateCurrentConversationData(
          updatedConversation,
          newDataModel,
          intent || 'unknown'
        );
        this.appState.agent.isHandlingQuery.set(false);
      }

      this.isProcessingSpeech = false;
      this.isPausedForTTS = true;

      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.pause();
      }
      if (this.mediaStream) {
        this.mediaStream.getAudioTracks().forEach(track => track.enabled = false);
      }

      await this.ttsService.streamTTS(response, this.audioElement!);

      if (this.mediaStream) {
        this.mediaStream.getAudioTracks().forEach(track => track.enabled = true);
      }
      await new Promise(resolve => setTimeout(resolve, this.ttsCooldown));
      this.isPausedForTTS = false;
      if (this.isRecording) this.startContinuousRecording();

    } catch (error) {
      console.error('error processing query');
      if (this.isRecording) {
        this.startContinuousRecording();
      }
    }
  }

  async playSingleResponseTTS(response: any) {
    this.appState.agent.isAgentSpeaking.set(true);
    await this.ttsService.streamTTS(response, this.audioElement!);
    this.appState.agent.singleTTS.set(null);
    this.appState.agent.isAgentSpeaking.set(false);
  }

  // Ensure mobile device timeout happens after idle duration
  private resetMobileIdleTimer(): void {
    if (this.mobileIdleTimeoutId) {
      clearTimeout(this.mobileIdleTimeoutId);
    }
    this.idleTimeoutDuration = this.isMobileSafari ? 40000 : 60000;
    this.mobileIdleTimeoutId = setTimeout(() => {
      if (this.isRecording) {
        this.stopContinuousRecording();
      }
    }, this.idleTimeoutDuration);
  }

  ngOnDestroy(): void {
    this.stopAllAudioComponents();
    if (this.audioElement?.src.startsWith('blob:')) {
      URL.revokeObjectURL(this.audioElement.src);
    }
    document.removeEventListener('visibilitychange', this.cleanupHandler);
    window.removeEventListener('pagehide', this.cleanupHandler);
    window.removeEventListener('beforeunload', this.cleanupHandler);
  }

  private cleanupOnExit(event: Event): void {
    this.stopAllAudioComponents();
    if (this.audioElement?.src.startsWith('blob:')) {
      URL.revokeObjectURL(this.audioElement.src);
    }
  }

  stopAllAudioComponents() {
    this.isRecording = false;
    this.isPausedForTTS = false;
    this.isProcessingSpeech = false;

    this.releaseRecordingResources();

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.analyser = null;
    }

    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
    }
  }

  private releaseRecordingResources(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.silenceCheckerId) {
      cancelAnimationFrame(this.silenceCheckerId);
      this.silenceCheckerId = null;
    }
  }

}
